import i18next from "i18next";
import { Constants } from "../models/Constants";
import { StatusCode } from "../models/Enums";
import { BaseMessageModel, ErrorModel } from "../models/ErrorModel";
import { ObjectivesSubObjectivesIndicatorsModel, QuestionModel, RespondentAndHealthFacilityType } from "../models/ShellTable/ShellTableModel";
import { getCall, postCall } from "./api";
import { instance } from "./instance";
import { notificationService } from "./notificationService";

export const shellTableService = {
    /** Upload shell table for questions */
    uploadFile: async (
        request: FormData,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<boolean> => {

        // Step 1: submit — server returns 202 { jobId }
        onProgress?.(`${i18next.t("Common.Uploading")}...`);
        const jobResponse: any = await postCall<FormData>(
            Constants.Api.Url.SHELL_TABLE_UPLOAD_FILE,
            request
        );

        const jobId: string = jobResponse?.jobId;
        if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

        // Step 2: poll until Succeeded or Failed
        const maxAttempts    = 900;      // 900 × 2s = 30 minutes max
        const pollIntervalMs = 2000;
        const stepVisibleMs  = 1000;     // hold each step label for at least 1s

        const BACKEND_STEPS = [
            "",                          // index 0 = "Uploading" placeholder
            "Parsing Excel data...",
            "Calculating results...",
            "Saving to database...",
        ];
        const stepTranslations: Record<string, string> = {
            "Parsing Excel data...":  i18next.t("Common.UploadStepParsingExcel") as string,
            "Calculating results...": i18next.t("Common.UploadStepCalculating") as string,
            "Saving to database...":  i18next.t("Common.UploadStepSaving") as string,
        };

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SHELL_TABLE_UPLOAD_FILE_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Assessment.DataCollection.ShellTable.Message.ShellTableUploadTemplateSuccessMessage"),
                    StatusCode.Created
                ));
                return true;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Assessment.DataCollection.ShellTable.Message.ShellTableUploadTemplateErrorMessage"),
                    StatusCode.PreConditionFailed
                ));
                throw new Error(msg as string);
            }
            // Queued | Processing → keep polling
        }

        throw new Error("Shell table processing timed out after 30 minutes.");
    },

    /** Gets respondent types and health facility types selected by specific assessmentId 
* * @param assessmentId A string value
*/
    getRespondentTypesHealthFacilityTypesByAssessmentId: (assessmentId: string) =>
        new Promise<RespondentAndHealthFacilityType>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.SHELL_TABLE_RESPONDENT_TYPES_HEALTH_FACILITY_TYPES.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(RespondentAndHealthFacilityType.init());
                }
            });
        }),

    /** Gets objectives, sub objectives, indicators selected by specific assessmentId 
* * @param assessmentId A string value
*/
    getObjectiveSubObjectivesIndicatorsAsync: (assessmentId: string) =>
        new Promise<ObjectivesSubObjectivesIndicatorsModel>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.SHELL_TABLE_OBJECTIVES_SUB_OBJECTIVES_INDICATORS.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(ObjectivesSubObjectivesIndicatorsModel.init());
                }
            });
        }),

    /** Gets questions selected by specific assessmentId, respondent type, geo graphical level, health facility type
* * @param assessmentId A string value
* * @param respondentType A number value
* * @param geoGraphicLevel A number value
* * @param healthFacilityType A number value
*/
    getQuestionsAsync: (assessmentId: string, respondentType: number, geoGraphicLevel: number, healthFacilityType: number) =>
        new Promise<Array<QuestionModel>>((resolve, reject) => {
            // call an api
            const url =
                Constants.Api.Url.SHELL_TABLE_QUESTIONS.replace(
                    "{0}",
                    assessmentId
                )
                    .replace("{1}", respondentType.toString())
                    .replace("{2}", geoGraphicLevel.toString())
                    .replace("{3}", healthFacilityType.toString());

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve([]);
                }
            });
        }),

    /** export questions selected by specific assessmentId, respondent type, health facility type
  * * @param assessmentId A string value
  * * @param respondentType A number value
  * * @param healthFacilityType A number value
  */
    exportQuestions: async (
        assessmentId: string,
        respondentType: number,
        healthFacilityType: number,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<Blob> => {
        onProgress?.(`${i18next.t("Common.Exporting")}...`);

        const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SHELL_TABLE_EXPORT_START
            .replace("{0}", assessmentId)
            .replace("{1}", respondentType.toString())
            .replace("{2}", healthFacilityType.toString())}`;
        const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
        const jobId: string = jobResponse?.data?.jobId;
        if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

        const BACKEND_STEPS = [
            "",
            "Fetching data...",
            "Generating file...",
        ];
        const stepTranslations: Record<string, string> = {
            "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
            "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
        };

        const exportSteps = [
            `${i18next.t("Common.Exporting")}...`,
            i18next.t("Common.ExportStepFetchingData") as string,
            i18next.t("Common.ExportStepGeneratingFile") as string,
        ];

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        const maxAttempts    = 900;
        const pollIntervalMs = 2000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SHELL_TABLE_EXPORT_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));
                const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SHELL_TABLE_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
                const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
                return downloadResponse.data as Blob;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Errors.SomethingWentWrong"),
                    StatusCode.PreConditionFailed
                ));
                throw new Error(msg as string);
            }
        }

        throw new Error("Shell table export timed out after 30 minutes.");
    },
}